import React, {useState, useRef, useEffect, useMemo} from 'react'
import {createPortal} from 'react-dom'
import {AnimatePresence, motion} from 'framer-motion'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Heading,
    Text,
    useDisclosure,
    useBreakpointValue,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    DrawerFooter,
    Center,
    Spinner,
    Flex,

    /* Hooks */
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {ChevronLeftIcon} from '../../../components/icons'
import {animationVariants} from './animation-variants'
import GridViewSelector from './grid-view-selector'
import DrawerSort from './drawer-sort'
import {useParams} from 'react-router-dom'
import {useSortingOptions} from '../../../hooks/use-sorting-options'
import {useRefinements} from '../../../hooks/use-refinements'
import {withCommerceSdkReact} from '@salesforce/retail-react-app/app/components/with-commerce-sdk-react/with-commerce-sdk-react'
import {useCategory} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getRootCategoryId} from '../../../utils/brandUtils'
import {useBrand} from '../../../contexts/brand-context'
import {FormattedMessage} from 'react-intl'
import {useHeaderContext} from '../../../contexts/header-context'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import NestedCategoryMenu from './nested-categories'
import Refinements from './refinements'
import FiltersHeader from './filters-header'
import ItemCount from './item-count'
import SelectedFiltersHeader from './selected-filters-header'
import {useStickyThreshold} from '../../../hooks/use-sticky-threshold'
import {HEADER_STICKY_SLOT_ID} from '../../../components/header/config'

const MotionBox = motion(Box)

const findCategoryPath = (node, targetId, path = []) => {
    if (!node) return null
    if (node.id === targetId) return [...path, node]
    for (const child of node.categories || []) {
        const result = findCategoryPath(child, targetId, [...path, node])
        if (result) return result
    }
    return null
}

const PlaceholderComponent = () => (
    <Center py={'20px'} px={'32px'} borderBottom={'1px solid'} borderColor={'gray.50'}>
        <Spinner size="md" />
    </Center>
)

// Attribute IDs that represent the "Brand" refinement group in SFCC.
const BRAND_ATTRIBUTE_IDS = ['brand', 'c_brand']

// Removes the current site's brand from any "Brand" refinement, and drops the
// refinement entirely when no other brand values remain. This hides the "Garage"
// option when the user is already browsing on the Garage brand (and vice versa),
// while keeping the group visible when a different brand is available to filter to.
const filterOutCurrentBrand = (refinements, brandId) => {
    if (!Array.isArray(refinements) || !refinements.length || !brandId) {
        return refinements || []
    }
    const currentBrand = String(brandId).toLowerCase()

    return refinements.reduce((acc, refinement) => {
        if (!BRAND_ATTRIBUTE_IDS.includes(refinement?.attributeId)) {
            acc.push(refinement)
            return acc
        }
        const filteredValues = (refinement.values || []).filter((v) => {
            const value = String(v?.value ?? '').toLowerCase()
            const label = String(v?.label ?? '').toLowerCase()
            return value !== currentBrand && label !== currentBrand
        })
        if (filteredValues.length > 0) {
            acc.push({...refinement, values: filteredValues})
        }
        return acc
    }, [])
}

const MobileSortSection = ({onSortClick, handleFilterClick, refinements, productSearchResult, gridView, setGridView, hideFiltersAndSort, showItemCount, hideSortAndGrid, styles}) => {
    const firstRefinementId =
        refinements && Object.keys(refinements).length > 0
            ? refinements[Object.keys(refinements)[0]]?.[0]?.attributeId
            : 'allFilters'

    return (
        <Flex justify={hideFiltersAndSort && !showItemCount ? 'flex-end' : 'space-between'} align="center" spacing={1} bg="white">
            {hideFiltersAndSort && showItemCount && <ItemCount count={productSearchResult?.total} />}
            {!hideFiltersAndSort && (
                <Flex alignItems="center">
                    <Button
                        colorScheme="black"
                        variant="filter"
                        onClick={() => handleFilterClick(firstRefinementId)}
                        display="inline-flex"
                    >
                        <Text as="span" {...styles.filterSortLabel}>
                            <FormattedMessage
                                id="product_list.button.filters_and_sort"
                                defaultMessage="Filters & Sort"
                            />
                        </Text>
                    </Button>
                    <ItemCount count={productSearchResult?.total} />
                </Flex>
            )}
            {!hideSortAndGrid && <GridViewSelector gridView={gridView} setGridView={setGridView} />}
        </Flex>
    )
}

MobileSortSection.propTypes = {
    onSortClick: PropTypes.func.isRequired,
    handleFilterClick: PropTypes.func.isRequired,
    refinements: PropTypes.object,
    productSearchResult: PropTypes.object,
    gridView: PropTypes.string,
    setGridView: PropTypes.func,
    hideFiltersAndSort: PropTypes.bool,
    showItemCount: PropTypes.bool,
    hideSortAndGrid: PropTypes.bool,
    styles: PropTypes.object
}

const PageFilters = ({
    category,
    productSearchResult,
    isLoading,
    resetFilters,
    toggleFilter: toggleFilterProp,
    onSortClick,
    isVisible = true,
    gridView,
    setGridView,
    hideFiltersAndSort = false,
    showItemCount = false,
    hideSortAndGrid = false,
    searchParams
}) => {
    const styles = useMultiStyleConfig('PageFilters')
    const {categoryId} = useParams()
    const {isOpen, onOpen, onClose} = useDisclosure()
    const {site} = useMultiSite()

    const [navDirection, setNavDirection] = useState('forward')
    const [activeFilter, setActiveFilter] = useState(null)
    const [categoryStack, setCategoryStack] = useState([category])

    const filterRefs = useRef({})

    const { brandId } = useBrand()
    const currentCategory = categoryStack[categoryStack.length - 1]
    const rootCategory = getRootCategoryId(site.id, brandId)
    const isRootCategory = currentCategory?.id === rootCategory
    const sizeGrouping = category?.c_sizeGrouping
    const showNoFilterMatch = !isLoading && productSearchResult && !productSearchResult?.hits

    const {sortingOptions, sortUrls, selectedSortingOption} = useSortingOptions(productSearchResult)

    const visibleRefinements = useMemo(
        () => filterOutCurrentBrand(productSearchResult?.refinements, brandId),
        [productSearchResult?.refinements, brandId]
    )

    const {groupedRefinements, selectedFilters, toggleFilter: toggleFilterFromHook} = useRefinements({
        refinements: visibleRefinements,
        sizeGrouping: sizeGrouping,
        category: category
    })

    // Use the toggleFilter from the parent (ProductList) if provided, as it uses the correct
    // URL generation (via buildUrl + getCategoryNavigationPath). This prevents the drawer from
    // closing when a filter is selected due to URL path mismatches.
    const toggleFilter = toggleFilterProp || toggleFilterFromHook

    const {shouldShowSticky} = useHeaderContext()
    const {filtersRef} = useStickyThreshold()
    const isMobile = useBreakpointValue({base: true, lg: false})

    // Resolved after mount only, so nothing is portaled during SSR
    const [stickySlot, setStickySlot] = useState(null)
    useEffect(() => {
        setStickySlot(document.getElementById(HEADER_STICKY_SLOT_ID))
    }, [])

    useEffect(() => {
        if (isOpen && activeFilter && filterRefs.current[activeFilter]) {
            setTimeout(() => {
                filterRefs.current[activeFilter]?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                })
            }, 100)
        }
    }, [isOpen, activeFilter])

    const handleFilterClick = (filterId) => {
        filterId = !(filterId?.indexOf('Size') > 0) ? filterId : 'c_size'
        setActiveFilter(filterId)

        if (filterId?.toLowerCase() === 'category') {
            const path =
                category && categoryId ? findCategoryPath(category, categoryId) : [category]
            const currentCat = path?.[path.length - 1]

            if (currentCat?.categories?.length) {
                setCategoryStack(path)
            } else if (currentCat?.parentCategoryId) {
                setCategoryStack([{id: currentCat.parentCategoryId}])
            } else {
                setCategoryStack(path || [category])
            }
        }

        onOpen()
    }

    const resetFiltersOnClose = () => {
        setActiveFilter(null)
        onClose()
    }

    const navigateBack = () => {
        setNavDirection('backward')
        if (categoryStack.length > 1) {
            setCategoryStack((prev) => prev.slice(0, -1))
        } else if (currentCategory?.parentCategoryId) {
            setCategoryStack([{id: currentCategory.parentCategoryId}])
        }
    }

    const SubCategoriesTree = withCommerceSdkReact(
        ({currentCategory, data, ...rest}) => {
            if (data?.id && data?.categories) {
                setCategoryStack([{...data, categories: data.categories}])
                setNavDirection('forward')
            }

            return (
                <NestedCategoryMenu
                    currentCategory={currentCategory}
                    navDirection={navDirection}
                    navigateBack={navigateBack}
                    onClose={onClose}
                    categoriesList={currentCategory?.categories || []}
                    CategoryTree={SubCategoriesTree}
                    withReactQuery={true}
                    {...rest}
                />
            )
        },
        {
            hook: useCategory,
            queryOptions: ({item}) => ({parameters: {id: item?.id || rootCategory}}),
            placeholder: PlaceholderComponent
        }
    )

    const renderDrawerContent = () => {
        if (isLoading) return <LoadingSpinner />

        if (activeFilter?.toLowerCase() === 'category') {
            return (
                <AnimatePresence mode="wait" initial={false} custom={navDirection}>
                    <MotionBox
                        key={currentCategory?.id || 'fallback'}
                        variants={animationVariants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        custom={navDirection}
                        transition={{duration: 0.1, ease: 'easeInOut'}}
                        position="relative"
                    >
                        {currentCategory?.name ? (
                            <NestedCategoryMenu
                                currentCategory={currentCategory}
                                navDirection={navDirection}
                                navigateBack={navigateBack}
                                onClose={onClose}
                                isRootCategory={isRootCategory}
                                categoriesList={currentCategory?.categories || []}
                                CategoryTree={SubCategoriesTree}
                                withReactQuery={false}
                            />
                        ) : (
                            <SubCategoriesTree item={currentCategory} />
                        )}
                    </MotionBox>
                </AnimatePresence>
            )
        }

        const sortSection = isMobile && sortUrls?.length > 0 && (
            <DrawerSort
                sortingOptions={sortingOptions}
                sortUrls={sortUrls}
                selectedSortingOption={selectedSortingOption}
            />
        )

        return (
            <>
                {sortSection}
                <Refinements
                    isLoading={isLoading}
                    toggleFilter={toggleFilter}
                    filters={visibleRefinements}
                    excludedFilters={['cgid']}
                    selectedFilters={selectedFilters}
                    initiallyExpandedFilters={[activeFilter]}
                    sizeGrouping={sizeGrouping}
                    filterRefs={filterRefs}
                    productSearchResult={productSearchResult}
                    category={category}
                />
            </>
        )
    }

    return (
        <>
            {isVisible && (
                <Box ref={filtersRef} {...styles.container} className="filters-header-parent">
                    {!isLoading && (
                        <Box
                            visibility={shouldShowSticky ? 'hidden' : 'visible'}
                            aria-hidden={shouldShowSticky ? true : undefined}
                        >
                            <FiltersHeader
                                handleFilterClick={handleFilterClick}
                                refinements={groupedRefinements}
                                sortingOptions={sortingOptions}
                                sortUrls={sortUrls}
                                selectedSortingOption={selectedSortingOption}
                                productSearchResult={productSearchResult}
                                gridView={gridView}
                                setGridView={setGridView}
                                hideFiltersAndSort={hideFiltersAndSort}
                                showItemCount={showItemCount}
                                hideSortAndGrid={hideSortAndGrid}
                                styles={styles}
                            />
                        </Box>
                    )}
                </Box>
            )}

            {isVisible &&
                shouldShowSticky &&
                !isLoading &&
                stickySlot &&
                createPortal(
                    <Box data-testid="page-filters-sticky-overlay" {...styles.stickyOverlay}>
                        <Box {...styles.stickyContainer}>
                            {isMobile ? (
                                <MobileSortSection
                                    onSortClick={onSortClick}
                                    handleFilterClick={handleFilterClick}
                                    refinements={groupedRefinements}
                                    productSearchResult={productSearchResult}
                                    gridView={gridView}
                                    setGridView={setGridView}
                                    hideFiltersAndSort={hideFiltersAndSort}
                                    showItemCount={showItemCount}
                                    hideSortAndGrid={hideSortAndGrid}
                                    styles={styles}
                                />
                            ) : (
                                <FiltersHeader
                                    handleFilterClick={handleFilterClick}
                                    refinements={groupedRefinements}
                                    sortingOptions={sortingOptions}
                                    sortUrls={sortUrls}
                                    selectedSortingOption={selectedSortingOption}
                                    productSearchResult={productSearchResult}
                                    containerProps={{mb: 0}}
                                    gridView={gridView}
                                    setGridView={setGridView}
                                    hideFiltersAndSort={hideFiltersAndSort}
                                    showItemCount={showItemCount}
                                    hideSortAndGrid={hideSortAndGrid}
                                    styles={styles}
                                />
                            )}
                        </Box>
                    </Box>,
                    stickySlot
                )}

            {!hideFiltersAndSort && <Drawer
                placement="right"
                isOpen={isOpen}
                blockScrollOnMount
                variant="filterDrawer"
                preserveScrollBarGap={false}
                size="sm"
                onClose={resetFiltersOnClose}
            >
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerHeader {...styles.drawerHeader}>
                        <Heading
                            as="p"
                            display={'flex'}
                            alignItems={'center'}
                            justifyContent={
                                activeFilter?.toLowerCase() === 'category'
                                    ? 'space-between'
                                    : 'start'
                            }
                            textAlign={'center'}
                            color={'black'}
                            fontWeight={'500'}
                            fontSize={'14px'}
                            textTransform={'uppercase'}
                        >
                            {activeFilter?.toLowerCase() === 'category' &&
                            !isRootCategory &&
                            currentCategory?.onlineSubCategoriesCount > 1 ? (
                                <Button
                                    mr={0}
                                    p={0}
                                    onClick={navigateBack}
                                    variant="ghost"
                                    size="sm"
                                >
                                    <ChevronLeftIcon color={'black'} />
                                </Button>
                            ) : (
                                <div />
                            )}

                            {isLoading && <LoadingSpinner />}
                            <Text isTruncated>
                                {activeFilter?.toLowerCase() === 'category' &&
                                    currentCategory?.name}
                            </Text>
                            {activeFilter?.toLowerCase() === 'category' && (
                                <Box data-testid="filters-button-close">
                                    <DrawerCloseButton position={'static'} />
                                </Box>
                            )}
                        </Heading>
                        {activeFilter?.toLowerCase() !== 'category' && (
                            <SelectedFiltersHeader
                                productSearchResult={productSearchResult}
                                toggleFilter={toggleFilter}
                                resetFilters={resetFilters}
                                onClose={onClose}
                                urlRefine={searchParams?.refine}
                            />
                        )}
                    </DrawerHeader>
                    <DrawerBody>{renderDrawerContent()}</DrawerBody>
                    {activeFilter?.toLowerCase() !== 'category' && (
                        <DrawerFooter {...styles.drawerFooter}>
                            <Button
                                variant="webPrimary"
                                width="100%"
                                onClick={onClose}
                                _focusVisible={{
                                    outline: '2px solid',
                                    outlineColor: 'webTheme.focus',
                                    outlineOffset: '2px'
                                }}
                                data-testid="filters-button-results"
                            >
                                {showNoFilterMatch ? (
                                    <FormattedMessage
                                        id="filters.no_match"
                                        defaultMessage="No result found"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="filters.see_results"
                                        defaultMessage="{count, plural, one {See result (#)} other {See results (#)}}"
                                        values={{count: productSearchResult?.total || 0}}
                                    />
                                )}
                            </Button>
                        </DrawerFooter>
                    )}
                </DrawerContent>
            </Drawer>}
        </>
    )
}

PageFilters.propTypes = {
    isLoading: PropTypes.bool,
    category: PropTypes.object,
    productSearchResult: PropTypes.object,
    resetFilters: PropTypes.func,
    toggleFilter: PropTypes.func,
    selectedSortingOptionLabel: PropTypes.object,
    selectedLabel: PropTypes.string,
    onSortClick: PropTypes.func,
    onFiltersClick: PropTypes.func,
    isVisible: PropTypes.bool,
    gridView: PropTypes.string,
    setGridView: PropTypes.func,
    hideFiltersAndSort: PropTypes.bool,
    showItemCount: PropTypes.bool,
    hideSortAndGrid: PropTypes.bool,
    searchParams: PropTypes.object
}

export default PageFilters
